import { useCreateNotification } from '@/hooks/mutation/useCreateNotification';
import { NotificationType, type CreateUserInmailRequest } from '@/types';
import { logger } from '@/utils/logger/logger';
import { Button } from '@heidi/design-system/button';
import { toast } from '@heidi/design-system/toast';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { NotificationGroup, NotificationStatus } from '@heidi/schema/ml-scribe';
import { useCallback } from 'react';

interface NotificationsDevSectionProps {
  /** Called after the user triggers a notification so the dev sheet can close. */
  onRequestClose?: () => void;
}

interface NotificationFixture {
  testId: string;
  label: string;
  payload: CreateUserInmailRequest;
}

// One fixture per `NotificationGroup` (`SESSION`, `TEMPLATE`, `TEAM`,
// `BILLING`, `TASK`, `GENERAL`), plus two `TEAM` variants because `notification-card.tsx`
// branches on `notification_name === NEW_INVITATION` vs falling back to
// `/team`, and an extra `SESSION` variant to exercise `SESSION_SHARED`'s
// share-tracking hook. `GENERAL` has no navigation target — clicking it
// just marks-as-read — but it's still worth firing so engineers can verify
// the no-nav branch and the dot/hover behaviour in isolation. `notification_name`
// is a free string on the API but the FE recognises the values in
// `NotificationType`; the other groups land on the generic no-action_link
// branches, which is what a fresh server-emitted notification looks like
// before any deep-link is attached.
const NOTIFICATION_FIXTURES: ReadonlyArray<NotificationFixture> = [
  {
    testId: 'dev-tools-notifications-fire-session-shared',
    label: 'Session shared',
    payload: {
      notification_name: NotificationType.SESSION_SHARED,
      notification_group: NotificationGroup.SESSION,
      title: 'Dev test — session shared',
      content: 'A test session was shared with you. Fired from the dev tools.',
      status: NotificationStatus.UNREAD,
    },
  },
  {
    testId: 'dev-tools-notifications-fire-session-expiring',
    label: 'Session expiring',
    payload: {
      notification_name: NotificationType.SESSION_EXPIRING,
      notification_group: NotificationGroup.SESSION,
      title: 'Dev test — session expiring',
      content: 'One of your test sessions is about to expire.',
      status: NotificationStatus.UNREAD,
    },
  },
  {
    testId: 'dev-tools-notifications-fire-template',
    label: 'Template',
    payload: {
      notification_name: 'template_updated',
      notification_group: NotificationGroup.TEMPLATE,
      title: 'Dev test — template update',
      content: 'A test template was updated. Fired from the dev tools.',
      status: NotificationStatus.UNREAD,
    },
  },
  {
    testId: 'dev-tools-notifications-fire-team-invitation',
    label: 'Team invitation',
    payload: {
      notification_name: NotificationType.NEW_INVITATION,
      notification_group: NotificationGroup.TEAM,
      title: 'Dev test — team invitation',
      content: 'You have been invited to a test team. Fired from the dev tools.',
      status: NotificationStatus.UNREAD,
    },
  },
  {
    testId: 'dev-tools-notifications-fire-team-role-change',
    label: 'Team role change',
    payload: {
      notification_name: NotificationType.ROLE_CHANGE,
      notification_group: NotificationGroup.TEAM,
      title: 'Dev test — role change',
      content: 'Your role on a test team was updated.',
      status: NotificationStatus.UNREAD,
    },
  },
  {
    testId: 'dev-tools-notifications-fire-billing',
    label: 'Billing',
    payload: {
      notification_name: 'billing_update',
      notification_group: NotificationGroup.BILLING,
      title: 'Dev test — billing update',
      content: 'A test billing event occurred. Fired from the dev tools.',
      status: NotificationStatus.UNREAD,
    },
  },
  {
    testId: 'dev-tools-notifications-fire-task-assigned',
    label: 'Task assigned',
    payload: {
      notification_name: NotificationType.TASK_ASSIGNED,
      notification_group: NotificationGroup.TASK,
      title: 'Dev test — task assigned',
      content: 'A task has been assigned to you. Fired from the dev tools.',
      status: NotificationStatus.UNREAD,
    },
  },
  {
    testId: 'dev-tools-notifications-fire-general',
    label: 'General',
    payload: {
      notification_name: 'general_announcement',
      notification_group: NotificationGroup.GENERAL,
      title: 'Dev test — general announcement',
      content: 'A test general notification. No navigation target on click.',
      status: NotificationStatus.UNREAD,
    },
  },
];

/**
 * Dev-tools sidebar section that fires a real test notification via the
 * `createUserInmail` API so engineers can iterate on the bell popover,
 * notification card, and read/unread interactions without waiting for
 * a real backend event (session share, team invite, etc.).
 *
 * Each chip posts a fixture covering one `NotificationGroup` (plus two
 * `TEAM` variants to cover the `NEW_INVITATION` action-link branch in
 * `notification-card.tsx`, and `GENERAL` to verify the no-navigation
 * row click). On success the list is refetched via the hook's `onSuccess`
 * (query invalidation), so reopening the bell shows the new row
 * immediately. The dev sheet closes after firing so the engineer can
 * verify by opening the bell.
 */
export function NotificationsDevSection({
  onRequestClose,
}: Readonly<NotificationsDevSectionProps>) {
  const { mutateAsync, isPending } = useCreateNotification();

  const handleFire = useCallback(
    async (fixture: NotificationFixture) => {
      try {
        await mutateAsync(fixture.payload);
        toast({
          tone: 'success',
          title: `Fired "${fixture.label}" notification`,
          description: 'Open the bell to see it. The list refreshes automatically.',
        });
        onRequestClose?.();
      } catch (error) {
        logger.error('Dev tools: failed to fire test notification', error);
        toast({
          tone: 'destructive',
          title: `Failed to fire "${fixture.label}" notification`,
          description: 'Check the console for details.',
        });
      }
    },
    [mutateAsync, onRequestClose]
  );

  const handleFireAll = useCallback(async () => {
    // Fire sequentially — the BE can race on simultaneous inmails for the
    // same user and we want the dev list to show seven distinct rows.
    let failed = 0;
    for (const fixture of NOTIFICATION_FIXTURES) {
      try {
        await mutateAsync(fixture.payload);
      } catch (error) {
        failed += 1;
        logger.error(
          `Dev tools: failed to fire test notification "${fixture.label}"`,
          error
        );
      }
    }
    if (failed === 0) {
      toast({
        tone: 'success',
        title: `Fired ${NOTIFICATION_FIXTURES.length} test notifications`,
        description: 'Open the bell to see them. The list refreshes automatically.',
      });
      onRequestClose?.();
    } else {
      toast({
        tone: 'destructive',
        title: `Fired ${NOTIFICATION_FIXTURES.length - failed} of ${NOTIFICATION_FIXTURES.length} test notifications`,
        description: `${failed} failed — check the console for details.`,
      });
    }
  }, [mutateAsync, onRequestClose]);

  return (
    <section aria-labelledby="dev-tools-notifications-heading" className="mt-4">
      <TypographyP2SemiBold id="dev-tools-notifications-heading" className="mb-2">
        Fire a test notification
      </TypographyP2SemiBold>
      <TypographyCaption className="mb-3 text-text-tertiary">
        Posts a real notification via <span className="font-mono">createUserInmail</span>{' '}
        so you can iterate on the bell popover, the notification card, and the read/unread
        flow without waiting for a real backend event. Covers each{' '}
        <span className="font-mono">NotificationGroup</span> (
        <span className="font-mono">SESSION</span>,{' '}
        <span className="font-mono">TEMPLATE</span>,{' '}
        <span className="font-mono">TEAM</span>,{' '}
        <span className="font-mono">BILLING</span>,{' '}
        <span className="font-mono">TASK</span>,{' '}
        <span className="font-mono">GENERAL</span>); the two{' '}
        <span className="font-mono">TEAM</span> variants exercise the
        <span className="font-mono"> NEW_INVITATION</span> action-link branch in{' '}
        <span className="font-mono">notification-card.tsx</span>, and{' '}
        <span className="font-mono">GENERAL</span> covers the no-navigation row click.
      </TypographyCaption>
      <div className="grid grid-cols-2 gap-2">
        {NOTIFICATION_FIXTURES.map((fixture) => (
          <Button
            key={fixture.testId}
            type="button"
            variant="outline"
            size="sm"
            className="w-full"
            disabled={isPending}
            onClick={() => {
              void handleFire(fixture);
            }}
            data-testid={fixture.testId}
          >
            {fixture.label}
          </Button>
        ))}
      </div>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="mt-2 w-full"
        disabled={isPending}
        onClick={() => {
          void handleFireAll();
        }}
        data-testid="dev-tools-notifications-fire-all"
      >
        Fire one of each ({NOTIFICATION_FIXTURES.length})
      </Button>
    </section>
  );
}
